"use client";
import React from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { type JSONContent } from "@tiptap/core";

import { ContentLinkPicker } from "@/components/ui/ContentLinkPicker";
import {
  ContentLink,
  EMPTY_DOC,
  normalizeBasePath,
  parseEditorValue,
  PinkLink,
} from "@/components/ui/rich-text-input/extensions";
import { RichTextLinkModal } from "@/components/ui/rich-text-input/RichTextLinkModal";
import { RichTextToolbarDock } from "@/components/ui/rich-text-input/RichTextToolbarDock";

type Props = {
  value?: string | JSONContent;
  onChange?: (content: { html: string; json: JSONContent }) => void;

  placeholder?: string;
  className?: string; // wrapper
  editorClassName?: string; // EditorContent
  minHeightClassName?: string; // e.g. "min-h-[12rem]"

  autoOpenPanel?: boolean; // default true
  disableScrollLockOnOpen?: boolean; // default false

  // For your internal content links (stored as mark attrs: { contentSlug })
  contentLinkBasePath?: string; // default "/content/"
  onContentLinkClick?: (slug: string) => void; // triggered on ctrl/cmd click in the editor
  dir?: "ltr" | "rtl";
};

function useIsSmallScreen() {
  const [isSmall, setIsSmall] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (event: MediaQueryListEvent) =>
      setIsSmall(event.matches);

    setIsSmall(mediaQuery.matches);

    if ("addEventListener" in mediaQuery) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    const legacyMediaQuery = mediaQuery as MediaQueryList & {
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };

    legacyMediaQuery.addListener?.(handleChange);
    return () => {
      legacyMediaQuery.removeListener?.(handleChange);
    };
  }, []);

  return isSmall;
}

export default function RichTextInput({
  value = EMPTY_DOC,
  onChange,
  placeholder = "",
  className = "",
  editorClassName = "",
  minHeightClassName = "min-h-fit",
  autoOpenPanel = true,
  disableScrollLockOnOpen = false,
  contentLinkBasePath = "/content/",
  onContentLinkClick,
  dir,
}: Props) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const isSmallScreen = useIsSmallScreen();
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [linkValue, setLinkValue] = React.useState("");
  const [contentPickerOpen, setContentPickerOpen] = React.useState(false);
  const [initialContentSlug, setInitialContentSlug] = React.useState("");
  const [isEmpty, setIsEmpty] = React.useState(true);

  const openLinkDialog = React.useCallback((prevHref?: string) => {
    setLinkValue(prevHref ?? "https://");
    setLinkDialogOpen(true);
  }, []);
  const closeLinkDialog = React.useCallback(() => setLinkDialogOpen(false), []);

  const openContentLinkDialog = React.useCallback(
    (prev?: { contentSlug?: string; contentId?: string }) => {
      setInitialContentSlug(prev?.contentSlug ?? "");
      setContentPickerOpen(true);
    },
    [],
  );
  const closeContentLinkDialog = React.useCallback(
    () => setContentPickerOpen(false),
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExt,
      ContentLink.configure({
        basePath: normalizeBasePath(contentLinkBasePath),
      }),
      PinkLink.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value ?? EMPTY_DOC,
    autofocus: false,
    immediatelyRender: false,
    editorProps: {
      handleClick: (_view, _pos, event) => {
        if (!(event instanceof MouseEvent)) return false;
        if (!(event.metaKey || event.ctrlKey)) return false;

        const target = event.target as Element | null;
        const el = target?.closest?.("a[data-content-slug]");
        if (!el) return false;

        const slug = el.getAttribute("data-content-slug")?.trim();
        if (!slug) return true;

        event.preventDefault();
        event.stopPropagation();
        onContentLinkClick?.(slug);
        return true;
      },
    },
    onUpdate({ editor }) {
      onChange?.({
        html: editor.getHTML(),
        json: editor.getJSON(),
      });
      setIsEmpty(editor.isEmpty);
    },
  });

  const [open, setOpen] = React.useState(false);

  // Keep external value in sync (supports TipTap JSON object OR JSON string OR legacy HTML string)
  React.useEffect(() => {
    if (!editor) return;

    const parsed = parseEditorValue(value);
    if (parsed.kind === "json") {
      const currentJson = editor.getJSON();
      if (JSON.stringify(currentJson) !== JSON.stringify(parsed.content)) {
        editor.commands.setContent(parsed.content, { emitUpdate: false });
      }
      return;
    }

    const currentHtml = editor.getHTML();
    if (parsed.content !== currentHtml) {
      editor.commands.setContent(parsed.content, { emitUpdate: false });
    }
  }, [value, editor]);

  // Keep empty state in sync for placeholder rendering
  React.useEffect(() => {
    if (!editor) return;
    const updateEmpty = () => setIsEmpty(editor.isEmpty);
    updateEmpty();
    editor.on("update", updateEmpty);
    editor.on("selectionUpdate", updateEmpty);
    return () => {
      editor.off("update", updateEmpty);
      editor.off("selectionUpdate", updateEmpty);
    };
  }, [editor]);

  React.useEffect(() => {
    if (!editor || !autoOpenPanel) return;

    const onFocus = () => setOpen(true);
    const onBlur = () => setOpen(false);

    editor.on("focus", onFocus);
    editor.on("blur", onBlur);
    return () => {
      editor.off("focus", onFocus);
      editor.off("blur", onBlur);
    };
  }, [editor, autoOpenPanel]);

  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (
      event: PointerEvent | MouseEvent | TouchEvent,
    ) => {
      const target = event.target as Node | null;
      if (!wrapperRef.current) return;
      if (linkDialogOpen || contentPickerOpen) return;
      if (target && wrapperRef.current.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest('[data-richtext-dock="true"]')
      )
        return;
      setOpen(false);
    };
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("touchstart", handlePointerDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("touchstart", handlePointerDown);
    };
  }, [open, linkDialogOpen, contentPickerOpen]);

  React.useEffect(() => {
    if (disableScrollLockOnOpen) return;
    if (!isSmallScreen) return;
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open, disableScrollLockOnOpen, isSmallScreen]);

  return (
    <div className={className} dir={dir} ref={wrapperRef}>
      <div className="relative rounded-md bg-white p-3">
        {placeholder && isEmpty ? (
          <div className="pointer-events-none absolute inset-0 px-3 py-2 text-sm text-gray-400 select-none">
            {placeholder}
          </div>
        ) : null}
        <EditorContent
          editor={editor}
          className={[
            "prose tiptap-editor max-w-none [&_*:focus]:outline-none",
            minHeightClassName,
            editorClassName,
          ].join(" ")}
        />
      </div>

      <RichTextToolbarDock
        open={open}
        editor={editor}
        onRequestLink={openLinkDialog}
        onRequestContentLink={openContentLinkDialog}
      />

      <RichTextLinkModal
        open={linkDialogOpen}
        value={linkValue}
        onChange={setLinkValue}
        onClose={closeLinkDialog}
        onApply={() => {
          if (!editor) return;
          const raw = linkValue.trim();
          const normalized =
            raw.startsWith("http://") || raw.startsWith("https://")
              ? raw
              : `https://${raw}`;
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: normalized })
            .run();
          closeLinkDialog();
        }}
      />

      <ContentLinkPicker
        open={contentPickerOpen}
        initialSlug={initialContentSlug}
        onClose={closeContentLinkDialog}
        onSelect={({ contentSlug, contentId }) => {
          if (!editor) return;
          const normalizedSlug = contentSlug.trim();
          if (!normalizedSlug) return;

          const payload: Record<string, any> = { contentSlug: normalizedSlug };
          if (contentId) {
            payload.contentId = contentId;
          }

          editor
            .chain()
            .focus()
            .extendMarkRange("contentLink")
            .setMark("contentLink", payload)
            .run();

          closeContentLinkDialog();
        }}
      />

      <style jsx global>{`
        /* Placeholder visibility */
        .tiptap-editor.ProseMirror p.is-editor-empty:first-child::before {
          color: #9ca3af;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .no-scrollbar {
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
